/**
 * Configuration Service
 * Automatically detects local vs cloud environment and sets appropriate API endpoints
 * Migrated from frontend/js/config.js
 */

type Environment = 'local' | 'cloud';

interface ConfigSettings {
  api: {
    baseUrl: string;
    fallbackUrls: string[];
  };
  features: {
    devMode: boolean;
    cors: boolean;
    logging: 'debug' | 'error';
  };
  environment: {
    name: Environment;
    autoDetection: boolean;
  };
}

class ConfigService {
  private environment: Environment;
  private config: ConfigSettings;
  private apiUrl: string = '';
  private apiBaseUrl: string = '';

  constructor() {
    this.environment = this.detectEnvironment();
    this.config = this.getConfig();
    // Initialize API URLs synchronously in constructor to avoid race conditions
    this.apiUrl = this.config.api.baseUrl;
    this.apiBaseUrl = `${this.apiUrl}/api/v1`;
  }

  /**
   * Detect if we're running locally or in the cloud
   */
  private detectEnvironment(): Environment {
    if (typeof window === 'undefined') {
      return 'local';
    }

    const hostname = window.location.hostname;
    const port = window.location.port;

    // Local development indicators
    const isLocalhost =
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname.endsWith('.local');
    const isLocalPort =
      port &&
      (port === '3000' ||
        port === '8000' ||
        port === '8080' ||
        port === '8081' ||
        port === '8086' ||
        port === '8087');

    // Cloud deployment indicators
    const isCloudRun = hostname.includes('run.app');
    const isVercel = hostname.includes('vercel.app');
    const isNetlify = hostname.includes('netlify.app');

    if (isLocalhost || isLocalPort) {
      return 'local';
    } else if (isCloudRun || isVercel || isNetlify) {
      return 'cloud';
    }

    // Default to cloud for unknown domains
    return 'cloud';
  }

  /**
   * Resolve the base URL for local dev.
   * Uses the env var on localhost; switches to relative URLs when the page is
   * served to another device on the LAN so Vite's /api proxy forwards requests
   * to the backend on the host machine.
   */
  private getLocalApiBaseUrl(): string {
    const envUrl = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || 'http://localhost:5006';
    if (typeof window === 'undefined') return envUrl;
    const host = window.location.hostname;
    const isOnLocalhost = host === 'localhost' || host === '127.0.0.1';
    return isOnLocalhost ? envUrl : '';
  }

  /**
   * Get configuration based on environment
   */
  private getConfig(): ConfigSettings {
    const configs: Record<Environment, ConfigSettings> = {
      local: {
        api: {
          // Local development - load from .env (root .env file).
          // When the dev server is accessed from another device on the LAN
          // (e.g. a phone at http://192.168.x.x:8086), fall back to relative
          // URLs so requests hit Vite's /api proxy instead of the device's
          // own localhost.
          baseUrl: this.getLocalApiBaseUrl(),
          fallbackUrls: [],
        },
        features: {
          devMode: true,
          cors: true,
          logging: 'debug',
        },
        environment: {
          name: 'local',
          autoDetection: true,
        },
      },
      cloud: {
        api: {
          // Production - use VITE_API_BASE_URL if set, otherwise use window.location.origin
          // This allows the frontend to work when served from the same origin as the backend
          baseUrl: import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || (typeof window !== 'undefined' ? window.location.origin : ''),
          fallbackUrls: [],
        },
        features: {
          devMode: false,
          cors: false,
          logging: 'error',
        },
        environment: {
          name: 'cloud',
          autoDetection: true,
        },
      },
    };

    return configs[this.environment];
  }

  /**
   * Test if an API endpoint is reachable
   */
  private async testApiEndpoint(
    url: string,
    timeout: number = 3000
  ): Promise<boolean> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      const response = await fetch(`${url}/health`, {
        method: 'GET',
        signal: controller.signal,
        mode: 'cors',
      });

      clearTimeout(timeoutId);
      return response.ok;
    } catch {
      return false;
    }
  }

  /**
   * Find the best available API URL
   */
  // @ts-ignore - unused method kept for future use
  private async findBestApiUrl(): Promise<string> {
    const { baseUrl, fallbackUrls } = this.config.api;

    // Test primary URL first
    if (await this.testApiEndpoint(baseUrl)) {
      this.log(`[OK] Primary API URL working: ${baseUrl}`);
      return baseUrl;
    }

    this.log(`[WARN] Primary API URL failed: ${baseUrl}, trying fallbacks...`);

    // Test fallback URLs
    for (const url of fallbackUrls) {
      if (await this.testApiEndpoint(url)) {
        this.log(`[OK] Fallback API URL working: ${url}`);
        return url;
      }
      this.log(`[ERROR] Fallback failed: ${url}`);
    }

    // NO FALLBACKS - Fail explicitly
    this.log(`[ERROR] No working API endpoint found. Check .env file configuration.`);
    this.log(`[ERROR] Expected API at: ${baseUrl}`);
    return baseUrl;
  }

  /**
   * Initialize configuration - set API URLs from environment
   */
  initialize(): Promise<ConfigService> {
    this.log(`[CHECK] Environment detected: ${this.environment}`);

    this.apiUrl = this.config.api.baseUrl;
    this.apiBaseUrl = `${this.apiUrl}/api/v1`;

    this.log(`[OK] Configuration initialized:`);
    this.log(`   Environment: ${this.environment}`);
    this.log(`   API URL: ${this.apiUrl}`);
    this.log(`   API Base: ${this.apiBaseUrl}`);

    return Promise.resolve(this);
  }

  /**
   * Logging based on environment
   */
  private log(message: string): void {
    const { logging } = this.config.features;

    if (
      logging === 'debug' ||
      (logging === 'error' && message.includes('[ERROR]'))
    ) {
      console.log(`[ConfigService] ${message}`);
    }
  }

  /**
   * Get current environment
   */
  getEnvironment(): Environment {
    return this.environment;
  }

  /**
   * Get API URL (without /api/v1)
   */
  getApiUrl(): string {
    return this.apiUrl || this.config.api.baseUrl;
  }

  /**
   * Get API Base URL (with /api/v1)
   */
  getApiBaseUrl(): string {
    if (!this.apiBaseUrl) {
      throw new Error('ConfigService not initialized. This should not happen.');
    }
    return this.apiBaseUrl;
  }

  /**
   * Check if running in local environment
   */
  isLocalEnvironment(): boolean {
    return this.environment === 'local';
  }

  /**
   * Check if running in cloud environment
   */
  isCloudEnvironment(): boolean {
    return this.environment === 'cloud';
  }

  /**
   * Check if development mode is enabled
   */
  isDevelopmentMode(): boolean {
    return this.config.features.devMode;
  }
}

// Create and export singleton instance
const configService = new ConfigService();

// Auto-initialize asynchronously
if (typeof window !== 'undefined') {
  configService.initialize().catch(console.error);
}

export default configService;
